/**
 * Owner: Masa Bayu
 * File: group-native-paragraphs.ts
 * Date:Time: 2026-10-05 12:58
 * Description: Fasa 8–10 · group body · first-line indent join · dropcap column.
 */

import {
  detectNativeDropCapPairs,
  nativeDropCapSkipIds,
} from './spsu-native-dropcap-face';
import type { TextElement } from '../extract/spsu-types';

/** Plat teks Native (SPSU absolute line). */
export type NativeLine = TextElement;

const X_ALIGN_TOL_PT = 2;
const FONT_SIZE_TOL_PT = 0.5;
/** Contoh Owner 1.2–1.5 · longgar untuk pdf.js body (~1.35–1.8em). */
const LINE_HEIGHT_MIN = 1.15;
const LINE_HEIGHT_MAX = 1.85;
const MIN_BODY_WIDTH = 160;
const MIN_BODY_WORDS = 5;
const MAX_BODY_FONT = 16;

/** Titik pemimpin TOC · atau elipsis. */
const TOC_DOT_LEADER = /\.{3,}|…|\.(?:\s*\.){2,}/;
/** Nombor halaman TOC (plat berasingan). */
const TOC_PAGE_NUM = /^\d{1,4}$/;
/** Penanda entri TOC (➢ dan saudara). */
const TOC_MARKER = /^[➢▸►▶•▪◆※➔➜➡➤➥➔]+$/u;

function sameX(a: NativeLine, b: NativeLine): boolean {
  return Math.abs(a.bbox.x - b.bbox.x) <= X_ALIGN_TOL_PT;
}

function sameFont(a: NativeLine, b: NativeLine): boolean {
  return Math.abs(a.style.fontSize - b.style.fontSize) <= FONT_SIZE_TOL_PT;
}

function consistentLineHeight(prev: NativeLine, next: NativeLine): boolean {
  const dy = next.bbox.y - prev.bbox.y;
  if (!(dy > 0)) return false;
  const em = prev.style.fontSize > 0 ? prev.style.fontSize : prev.bbox.height;
  if (!(em > 0)) return false;
  const ratio = dy / em;
  return ratio >= LINE_HEIGHT_MIN && ratio <= LINE_HEIGHT_MAX;
}

function isPageNumberPlate(line: NativeLine): boolean {
  return TOC_PAGE_NUM.test(line.content.trim());
}

/**
 * Fasa 10 · Type Filter TOC.
 * Baris dengan pemimpin titik, nombor halaman kanan, atau penanda ➢
 * kekal absolute — Core Engine tidak boleh justify.
 */
export function isTocStyleLine(
  line: NativeLine,
  all: readonly NativeLine[],
): boolean {
  const trimmed = line.content.trim();
  if (!trimmed) return false;
  if (TOC_DOT_LEADER.test(trimmed)) return true;
  if (isPageNumberPlate(line)) return true;
  if (TOC_MARKER.test(trimmed)) return true;
  if (TOC_DOT_LEADER.test(trimmed) && /\d{1,4}\s*$/.test(trimmed)) return true;

  const em = line.style.fontSize > 0 ? line.style.fontSize : line.bbox.height;
  const yTol = Math.max(3, em * 0.4);
  const lookAhead = em * LINE_HEIGHT_MAX;
  const titleRight = line.bbox.x + line.bbox.width;

  for (const other of all) {
    if (other.id === line.id) continue;
    const dy = other.bbox.y - line.bbox.y;
    const otherIsPage = isPageNumberPlate(other);
    const toTheRight = other.bbox.x >= titleRight - 1;

    if (otherIsPage && Math.abs(dy) <= yTol && toTheRight) return true;
    // Wrap TOC: tajuk baris 1 · nombor pada baris 2.
    if (otherIsPage && dy > 0 && dy <= lookAhead && toTheRight) return true;

    if (
      TOC_MARKER.test(other.content.trim()) &&
      Math.abs(dy) <= yTol &&
      other.bbox.x < line.bbox.x
    ) {
      return true;
    }
  }
  return false;
}

/** Tajuk / caption / TOC kekal absolute · jangan masuk Core Engine. */
export function isBodyLineCandidate(
  line: NativeLine,
  all: readonly NativeLine[] = [],
): boolean {
  const trimmed = line.content.trim();
  if (trimmed.length < 12) return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < MIN_BODY_WORDS) return false;
  if (line.bbox.width < MIN_BODY_WIDTH) return false;
  if (!(line.style.fontSize > 0) || line.style.fontSize >= MAX_BODY_FONT) return false;
  if (all.length > 0 && isTocStyleLine(line, all)) return false;
  return true;
}

function canAppendBody(
  prev: NativeLine,
  next: NativeLine,
  all: readonly NativeLine[],
): boolean {
  if (!isBodyLineCandidate(next, all)) return false;
  if (!sameFont(prev, next) || !consistentLineHeight(prev, next)) return false;
  if (sameX(prev, next)) return true;
  // First-line indent (x lebih besar) → baris seterusnya di tepi lajur (x lebih kecil).
  if (prev.bbox.x > next.bbox.x + X_ALIGN_TOL_PT) return true;
  return false;
}

/**
 * Kumpul plat absolute → perenggan body.
 * Dropcap digabung dahulu; tajuk/caption kekal singleton absolute.
 */
export function groupNativeLinesIntoParagraphs(
  lines: readonly NativeLine[],
): NativeLine[][] {
  if (lines.length === 0) return [];

  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const sorted = [...lines].sort(
    (a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x,
  );
  const pairs = detectNativeDropCapPairs(sorted);
  const dropSkip = nativeDropCapSkipIds(pairs);
  const consumed = new Set<string>();
  const groups: NativeLine[][] = [];

  for (const pair of pairs) {
    const group: NativeLine[] = [pair.letter, pair.host];
    for (const id of pair.absorbIds) {
      const plate = byId.get(id);
      if (plate) group.push(plate);
    }
    group.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
    for (const el of group) consumed.add(el.id);

    let cursor = group.filter((g) => g.id !== pair.letter.id).at(-1) ?? pair.host;
    for (const cand of sorted) {
      if (consumed.has(cand.id)) continue;
      if (cand.bbox.y <= cursor.bbox.y) continue;
      const em = cursor.style.fontSize > 0 ? cursor.style.fontSize : 11;
      if (cand.bbox.y - cursor.bbox.y > em * LINE_HEIGHT_MAX) break;
      if (!isBodyLineCandidate(cand, sorted)) break;
      if (!sameFont(cursor, cand) || !consistentLineHeight(cursor, cand)) break;
      // Host indent (wrap beside dropcap) atau tepi lajur (letter.x) selepas wrap.
      const atHost = Math.abs(cand.bbox.x - pair.host.bbox.x) <= X_ALIGN_TOL_PT;
      const atColumn =
        Math.abs(cand.bbox.x - pair.letter.bbox.x) <= X_ALIGN_TOL_PT;
      if (!atHost && !atColumn) break;
      group.push(cand);
      consumed.add(cand.id);
      cursor = cand;
    }
    groups.push(group);
  }

  let current: NativeLine[] | null = null;
  for (const line of sorted) {
    if (consumed.has(line.id) || dropSkip.has(line.id)) continue;
    if (!isBodyLineCandidate(line, sorted)) {
      /* EKOR perenggan · baris pendek yang ragged di tepi lajur (fon &
       * tinggi baris sepadan, x sama) ialah pengakhiran perenggan yang
       * sedang berjalan · BUKAN perenggan baharu. Tanpa ini, baris penuh
       * sebelumnya menjadi singleton dan Design D melabelnya 'single'
       * → left (khalifah P1: 292pt mencecah tepi kanan lajur). */
      const tail = current?.[current.length - 1];
      if (
        tail !== undefined &&
        sameFont(tail, line) &&
        consistentLineHeight(tail, line) &&
        sameX(tail, line) &&
        !isTocStyleLine(line, sorted)
      ) {
        current!.push(line);
        continue;
      }
      groups.push([line]);
      continue;
    }
    if (current && canAppendBody(current[current.length - 1]!, line, sorted)) {
      current.push(line);
      continue;
    }
    current = [line];
    groups.push(current);
  }

  groups.sort(
    (a, b) =>
      (a[0]?.bbox.y ?? 0) - (b[0]?.bbox.y ?? 0) ||
      (a[0]?.bbox.x ?? 0) - (b[0]?.bbox.x ?? 0),
  );

  return groups;
}

/** Kumpulan yang patut ke Core Engine (>1 baris body / dropcap+host). */
export function isEngineParagraphGroup(group: readonly NativeLine[]): boolean {
  if (group.length < 2) return false;
  const bodyPlates = group.filter(
    (g) => g.content.trim().length !== 1 || !/^\p{L}$/u.test(g.content.trim()),
  );
  return bodyPlates.length >= 2 || group.length >= 2;
}
